/**
 * Speed lines for the v1.1 chase camera (concept L847–L853, L1471–L1480): 80 streaks in camera space rushing
 * past, brightening from 56 to 70 m/s. Preallocated; updating allocates nothing.
 */
import {
  AdditiveBlending,
  BufferAttribute,
  BufferGeometry,
  DynamicDrawUsage,
  LineBasicMaterial,
  LineSegments,
} from 'three';
import { sstep } from '../core/math.ts';
import type { Rng } from '../core/rng.ts';

const TAU = Math.PI * 2;
export const SPEED_LINES = 80;

export class SpeedLines {
  readonly lines: LineSegments<BufferGeometry, LineBasicMaterial>;
  private readonly a = new Float64Array(SPEED_LINES);
  private readonly r = new Float64Array(SPEED_LINES);
  private readonly z = new Float64Array(SPEED_LINES);
  private readonly len = new Float64Array(SPEED_LINES);
  private readonly pos = new Float32Array(SPEED_LINES * 6);

  /** Draws from `rand` in the concept's order (angle, radius, depth, length per line). */
  constructor(rand: Rng) {
    for (let i = 0; i < SPEED_LINES; i++) {
      this.a[i] = rand() * TAU;
      this.r[i] = 7 + rand() * 12;
      this.z[i] = -rand() * 90;
      this.len[i] = 2 + rand() * 5;
    }
    const g = new BufferGeometry();
    g.setAttribute('position', new BufferAttribute(this.pos, 3).setUsage(DynamicDrawUsage));
    this.lines = new LineSegments(
      g,
      new LineBasicMaterial({
        color: 0xfff4e6,
        transparent: true,
        opacity: 0,
        blending: AdditiveBlending,
        depthWrite: false,
        fog: false,
      }),
    );
    this.lines.frustumCulled = false;
  }

  update(dt: number, speed: number, rand: Rng): void {
    const sp = sstep(56, 70, speed);
    this.lines.material.opacity = 0.03 + 0.13 * sp;
    const p = this.pos;
    for (let i = 0; i < SPEED_LINES; i++) {
      let z = (this.z[i] ?? 0) + speed * 1.4 * dt;
      if (z > -3) {
        z = -70 - rand() * 30;
        this.a[i] = rand() * TAU;
      }
      this.z[i] = z;
      const a = this.a[i] ?? 0;
      const r = this.r[i] ?? 0;
      const x = Math.cos(a) * r;
      const y = Math.sin(a) * r * 0.65;
      const o = i * 6;
      p[o] = x;
      p[o + 1] = y;
      p[o + 2] = z;
      p[o + 3] = x;
      p[o + 4] = y;
      p[o + 5] = z - (this.len[i] ?? 0) * (0.6 + sp);
    }
    const attr = this.lines.geometry.getAttribute('position');
    attr.needsUpdate = true;
  }
}
